// @flow
import React from 'react';
import styled from 'styled-components';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { colors } from 'bench-style-constants';
import { debounce } from 'lodash';
import { rgba } from 'color-helpers';
import { TextBlock, Button, Text } from 'bench-internal-theme';
import { TextInput } from 'bench-internal-theme/form-input-fields';
// $FlowFixMe - typescript file
import AddonSidebar from './addon-sidebar/AddonSidebar';
import LedgerAccountSidebar from './ledger-account-sidebar/LedgerAccountSidebar';
import ledgerAccountsTabs from './ledgerAccountsTabs';
import type { LedgerAccountsTabs } from './types';

const LedgerAccountsHeaderLayout = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const LedgerAccountsHeaderControlsLayout = styled.div`
  align-items: center;
  display: grid;
  grid-column-gap: 20px;
  grid-template-columns: max-content 320px 100px repeat(3, max-content);
  left: 0;
  padding: 24px 32px 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const LedgerAccountsHeaderNavigationLayout = styled.div`
  border-bottom: 1px solid ${colors.silt};
  bottom: 0;
  display: flex;
  left: 0;
  padding: 0 8px;
  position: absolute;
  right: 0;
`;

const Tab = styled.button`
  background: transparent;
  border-radius: 0;
  border: 0 none;
  box-shadow: none;
  color: ${colors.dust};
  cursor: pointer;
  display: block;
  font-size: 16px;
  line-height: 16px;
  padding: 8px 24px;
  position: static;

  &:focus {
    background: ${rgba(colors.brook, 0.75)};
  }

  ${({ isActive }) =>
    isActive &&
    `
    box-shadow: 0 1px ${colors.water};
    color: ${colors.water};
  `};
`;

type LedgerAccountsHeaderProps = {
  search: string,
  showAccountsOnly: boolean,
  showActiveOnly: boolean,
  tab: LedgerAccountsTabs,
  updateQueryParams: Object => void,
};

const LedgerAccountsHeader = ({
  search,
  showAccountsOnly,
  showActiveOnly,
  tab,
  updateQueryParams,
}: LedgerAccountsHeaderProps) => {
  const updateSearch = updatedSearch => updateQueryParams({ search: updatedSearch });

  const onClickNew = (closeSidebar, openSidebar) => {
    if (tab === ledgerAccountsTabs.LEDGER_ACCOUNTS) {
      return openSidebar(<LedgerAccountSidebar closeSidebar={closeSidebar} />);
    }
    return openSidebar(<AddonSidebar closeSidebar={closeSidebar} />);
  };

  return (
    <LedgerAccountsHeaderLayout>
      <LedgerAccountsHeaderControlsLayout>
        <TextBlock styleType="title">Ledger Accounts</TextBlock>
        <TextInput
          value={search}
          onChange={event => updateSearch(event.target.value)}
          placeholder={
            tab === ledgerAccountsTabs.LEDGER_ACCOUNTS
              ? 'Search ledger account name'
              : 'Search add-on name'
          }
        />
        <AppSidebar>
          {({ closeSidebar, openSidebar }) => (
            <Button
              onClick={() => onClickNew(closeSidebar, openSidebar)}
              size="medium"
              role="button"
              aria-label={tab === ledgerAccountsTabs.LEDGER_ACCOUNTS ? 'New Ledger' : 'New Add-on'}
              styleType="primary"
            >
              {tab === ledgerAccountsTabs.LEDGER_ACCOUNTS ? 'New Ledger' : 'New Add-on'}
            </Button>
          )}
        </AppSidebar>
        {tab === ledgerAccountsTabs.LEDGER_ACCOUNTS && (
          <>
            <Text uppercase>Show:</Text>
            <Button
              icon={showActiveOnly ? 'squareChecked' : 'squareOutlined'}
              onClick={() => updateQueryParams({ showActiveOnly: !showActiveOnly })}
              styleType="total-ghost"
            >
              Active Only
            </Button>
            <Button
              icon={showAccountsOnly ? 'squareChecked' : 'squareOutlined'}
              onClick={() => updateQueryParams({ showAccountsOnly: !showAccountsOnly })}
              styleType="total-ghost"
              aria-label="Show Accounts Only"
            >
              Accounts Only
            </Button>
          </>
        )}
      </LedgerAccountsHeaderControlsLayout>
      <LedgerAccountsHeaderNavigationLayout>
        <Tab
          isActive={tab === ledgerAccountsTabs.LEDGER_ACCOUNTS}
          onClick={() => updateQueryParams({ tab: ledgerAccountsTabs.LEDGER_ACCOUNTS })}
        >
          Ledgers & Accounts
        </Tab>
        <Tab
          isActive={tab === ledgerAccountsTabs.ADDONS}
          onClick={() => updateQueryParams({ tab: ledgerAccountsTabs.ADDONS })}
        >
          Add-ons
        </Tab>
      </LedgerAccountsHeaderNavigationLayout>
    </LedgerAccountsHeaderLayout>
  );
};

export default LedgerAccountsHeader;
